// Basic LCS-based diff for arrays
function diffArrays<T>(
  arr1: T[],
  arr2: T[]
): { type: 'removed' | 'added' | 'unchanged'; value: T[] }[] {
  const n = arr1.length;
  const m = arr2.length;

  // Initialize DP table
  const dp: number[][] = [];
  for (let i = 0; i <= n; i++) {
    dp[i] = new Array(m + 1).fill(0);
  }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const val1 = arr1[i - 1];
      const val2 = arr2[j - 1];

      const prevDiag = dp[i - 1]?.[j - 1] ?? 0;
      const prevUp = dp[i - 1]?.[j] ?? 0;
      const prevLeft = dp[i]?.[j - 1] ?? 0;

      const currentRow = dp[i];
      if (currentRow) {
        if (val1 === val2) {
          currentRow[j] = prevDiag + 1;
        } else {
          currentRow[j] = Math.max(prevUp, prevLeft);
        }
      }
    }
  }

  const result: { type: 'removed' | 'added' | 'unchanged'; value: T[] }[] = [];
  let i = n;
  let j = m;

  while (i > 0 || j > 0) {
    const currentRow = dp[i];
    const upRow = dp[i - 1];

    const upVal = (i > 0 && upRow && upRow[j] !== undefined ? upRow[j] : -1) as number;
    const leftVal = (
      j > 0 && currentRow && currentRow[j - 1] !== undefined ? currentRow[j - 1] : -1
    ) as number;

    if (i > 0 && j > 0 && arr1[i - 1] === arr2[j - 1]) {
      const val = arr1[i - 1];
      if (val !== undefined) {
        if (result.length > 0 && result[0] && result[0].type === 'unchanged') {
          result[0].value.unshift(val);
        } else {
          result.unshift({ type: 'unchanged', value: [val] });
        }
      }
      i--;
      j--;
    } else if (j > 0 && (i === 0 || leftVal >= upVal)) {
      const val = arr2[j - 1];
      if (val !== undefined) {
        if (result.length > 0 && result[0] && result[0].type === 'added') {
          result[0].value.unshift(val);
        } else {
          result.unshift({ type: 'added', value: [val] });
        }
      }
      j--;
    } else {
      const val = arr1[i - 1];
      if (val !== undefined) {
        if (result.length > 0 && result[0] && result[0].type === 'removed') {
          result[0].value.unshift(val);
        } else {
          result.unshift({ type: 'removed', value: [val] });
        }
      }
      i--;
    }
  }

  return result;
}

// Robust sentence splitter
export function splitSentences(text: string): string[] {
  if (!text) {
    return [];
  }

  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'sentence' });
    const segments = Array.from(segmenter.segment(text));
    return segments.map((s) => s.segment);
  }

  return text.match(/[^.!?\n]+([.!?\n]+(\s+|$)|$)/g) || [text];
}

export type DiffChange = {
  type: 'removed' | 'added' | 'unchanged';
  text: string;
};

export function computeSentenceDiff(oldText: string, newText: string): DiffChange[] {
  const oldSentences = splitSentences(oldText);
  const newSentences = splitSentences(newText);

  const diffs = diffArrays(oldSentences, newSentences);

  return diffs.map((d) => ({
    type: d.type,
    text: d.value.join(''),
  }));
}
